import type { ReactNode } from 'react';

/**
 * Shared inline notice. Replaces the ad-hoc `<p className="text-red-400">`
 * scattered across the app so every response reads as a considered card
 * (icon + subdued tint) rather than bare alarm-red text.
 *
 * Colours follow the app's palette convention (see tailwind.config.js): the
 * custom single-token hues (amber, emerald) are used bare with an /opacity
 * modifier — `bg-amber/10` — exactly as billing.tsx does; the hues that only
 * exist as default Tailwind scales (red, sky) use the numbered scale. Writing
 * a numbered shade of a custom token (e.g. `amber-500`) would resolve to the
 * DEFAULT scale, not the brand token — so we don't.
 *
 * This is presentational only. For a caught API error prefer <ApiErrorNotice>,
 * which routes plan-gate (402) responses to the upgrade prompt instead of here.
 */

export type AlertVariant = 'error' | 'warning' | 'info' | 'success';

const STYLES: Record<AlertVariant, { card: string; icon: string; path: ReactNode }> = {
  error: {
    card: 'border-red-500/40 bg-red-500/10 text-red-300',
    icon: 'text-red-400',
    // x in a circle
    path: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15 9-6 6M9 9l6 6" />
      </>
    ),
  },
  warning: {
    card: 'border-amber/40 bg-amber/10 text-amber',
    icon: 'text-amber',
    // triangle-exclamation
    path: (
      <>
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4M12 17h.01" />
      </>
    ),
  },
  info: {
    card: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
    icon: 'text-sky-400',
    // info circle
    path: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 16v-4M12 8h.01" />
      </>
    ),
  },
  success: {
    card: 'border-emerald/40 bg-emerald/10 text-emerald',
    icon: 'text-emerald',
    // check circle
    path: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
  },
};

export function Alert({
  variant = 'error',
  title,
  children,
  action,
  compact = false,
  className,
}: {
  variant?: AlertVariant;
  /** Optional bold lead line above the body. */
  title?: string;
  children?: ReactNode;
  /** Optional trailing control (e.g. a retry button or link). */
  action?: ReactNode;
  /** Tighter padding + smaller type, for errors shown inline within a row. */
  compact?: boolean;
  className?: string;
}): JSX.Element {
  const s = STYLES[variant];
  // Errors and warnings are assertive (screen-reader interrupts); info/success
  // are polite status updates.
  const role = variant === 'error' || variant === 'warning' ? 'alert' : 'status';
  const box = compact ? 'gap-2 px-2.5 py-1.5 text-xs' : 'gap-3 px-4 py-3 text-sm';
  const iconSize = compact ? 'h-3.5 w-3.5' : 'mt-0.5 h-4 w-4';
  return (
    <div role={role} className={`flex items-start rounded-lg border ${box} ${s.card} ${className ?? ''}`}>
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`shrink-0 ${iconSize} ${s.icon}`}
      >
        {s.path}
      </svg>
      <div className="min-w-0 flex-1 space-y-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className="leading-snug">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
